import { Flex, useColorModeValue } from '@chakra-ui/react';
import React from 'react';
import Select, {
  components,
  StylesConfig,
  ValueContainerProps,
} from 'react-select';

import { colors } from '../colors';
import { Icon } from './icons';

interface Options {
  readonly value: string;
  readonly label: string;
}

const ValueContainer = ({
  children,
  ...props
}: ValueContainerProps<Options>) => {
  return (
    components.ValueContainer && (
      <components.ValueContainer {...props}>
        <Flex alignItems="center" w="100%">
          <Icon
            icon="magnifying-glass"
            size="16px"
            color={colors.black[70]}
            style={{ margin: '0 16px' }}
          />
          {children}
        </Flex>
      </components.ValueContainer>
    )
  );
};

const selectStyles = ({
  bgColor,
  borderColor,
  fontColor,
  fontWeight,
  isSelectedColor = '#5367a0',
  isFocusedColor = '#ffffff0f',
  menuPadding,
}: {
  bgColor: string;
  borderColor: string;
  fontColor: string;
  fontWeight: number;
  isSelectedColor?: string;
  isFocusedColor?: string;
  menuPadding?: string;
}): StylesConfig<Options> => ({
  clearIndicator: (styles) => ({ ...styles }),
  container: (styles) => ({
    ...styles,
    minWidth: '300px',
    width: '100%',
  }),
  control: (styles, { isDisabled }) => ({
    ...styles,
    backgroundColor: '#00000000',
    borderColor,
    color: '#ffffffeb',
    fontWeight,
    borderRadius: '0.375rem',
    opacity: isDisabled ? '0.4' : 'none',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: bgColor,
    padding: menuPadding,
  }),
  input: (styles) => ({ ...styles, color: fontColor }),
  option: (styles, { isFocused, isSelected, isDisabled }) => {
    let backgroundColor;
    if (isSelected) {
      backgroundColor = isSelectedColor;
    } else {
      backgroundColor = isFocused ? isFocusedColor : '#00000000';
    }
    return {
      ...styles,
      backgroundColor: isDisabled ? undefined : backgroundColor,
      color: isDisabled ? 'grey' : fontColor,
      cursor: isDisabled ? 'not-allowed' : 'default',
    };
  },
  singleValue: (styles) => ({
    ...styles,
    color: fontColor,
  }),
});

type SearchSelectProps = {
  value?: Options;
  onChange: (nextValue: string) => void;
  options: { label: string; value: string; isDisabled?: boolean }[];
  isDisabled?: boolean;
  isLoading?: boolean;
  isClearable?: boolean;
  isRightToLeft?: boolean;
  placeholder?: string;
  displaySearchIcon?: boolean;
  isSelectedColor?: string;
  isFocusedColor?: string;
  menuPadding?: string;
  fontWeight?: number;
  name?: string;
};

export function SearchSelect({
  value,
  onChange,
  options,
  isDisabled = false,
  isLoading = false,
  isClearable = false,
  isRightToLeft = false,
  placeholder,
  displaySearchIcon = false,
  isSelectedColor,
  isFocusedColor,
  menuPadding,
  fontWeight,
  name,
}: SearchSelectProps) {
  const bgColor = useColorModeValue(colors.background[50], '#394B59');
  const borderColor = useColorModeValue('gray.100', '#ffffff29');
  const fontColor = useColorModeValue('black', 'white');

  return (
    <Select
      name={name}
      value={value}
      isDisabled={isDisabled}
      isLoading={isLoading}
      isClearable={isClearable}
      isRtl={isRightToLeft}
      isSearchable
      placeholder={placeholder}
      onChange={(newValue) => {
        const newVal = newValue
          ? (newValue as { value: string; label: string })
          : { value: '', label: '' };

        onChange(newVal.value);
      }}
      options={options}
      components={displaySearchIcon ? { ValueContainer } : undefined}
      styles={selectStyles({
        bgColor,
        borderColor,
        fontColor,
        fontWeight: fontWeight ?? 600,
        isSelectedColor,
        isFocusedColor,
        menuPadding,
      })}
    />
  );
}
